import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SelectTypes } from 'components/Select/Select.props'
import {
  DiscardedAppointment,
  AppointmentCategory,
  UserAppointment,
  BookingNowData,
} from 'models/appointment.model'
import { LocationDetail } from 'models/location'
import {
  UserAppointmentResponse,
  AppointmentsResponse,
  DiscardedAppointmentResponse,
  locationResponse,
  getSingleAppointmentResponse,
  AppointmentDetail,
  ReservationResponse,
  getOpenCourtsResponse,
} from 'services/appointment'
import APILoadingStatus from 'types/api-loading-status'
import { EventCalendar } from 'types/event-calendar-types'
import asyncReducers from '../../../utils/async-reducers'
import {
  getDiscardedAppointments,
  postRestoreAppointment,
  getUserAppointments,
  getAppointments,
  getLocation,
  updateLocation,
  getAppointmentCategories,
  getSingleAppointment,
  updateAppointment,
  getAppointmentColors,
  deleteAppointment,
  getOpenCourts,
} from './appointments.actions'
import toast from 'react-hot-toast'
import { sortBy } from 'lodash'

// Helper to safely handle error messages that can be string or boolean
const getErrorMessage = (error: string | boolean): string =>
  typeof error === 'string' ? error : 'An error occurred'

export type AppointmentState = {
  loading: APILoadingStatus
  loadingAppointment: APILoadingStatus
  loadingOpenCourts: APILoadingStatus
  discarded: DiscardedAppointment[] | null
  userAppointments: UserAppointment[]
  currentPage: number
  totalAppointments: number
  appointments: EventCalendar[] | null
  // requestId of the latest getAppointments call — used to drop a stale
  // response (e.g. a slow fetch for the previous day resolving after the
  // current day's) so it can't paint under the new date header (product#502).
  appointmentsReqId: string | null
  location: LocationDetail | null
  courts: { id: string; title: string }[] | null
  categories: SelectTypes[]
  currentAppointment: AppointmentDetail | null
  reservationColors: SelectTypes[]
  bookingNowData: BookingNowData | null
}

const initialState: AppointmentState = {
  loading: APILoadingStatus.Idle,
  loadingAppointment: APILoadingStatus.Idle,
  loadingOpenCourts: APILoadingStatus.Idle,
  discarded: null,
  userAppointments: [],
  currentPage: 1,
  totalAppointments: 0,
  appointments: null,
  appointmentsReqId: null,
  location: null,
  courts: null,
  categories: [],
  currentAppointment: null,
  reservationColors: [],
  bookingNowData: null,
}

const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },
    // Clear the calendar's appointments (used on date change so the previous
    // day's events don't linger under the new date header while the new day
    // loads — product#502).
    clearAppointments(state) {
      state.appointments = null
    },
    // Re-emit the current (server-truth) appointments with a fresh array
    // reference. Used to roll back a rejected drag-move: the slice still holds
    // the pre-move state, so a new reference makes the Calendar's `events` prop
    // change identity and re-sync — reverting the optimistic move without a
    // network round-trip (product#502).
    resyncAppointments(state) {
      state.appointments = state.appointments ? [...state.appointments] : state.appointments
    },
    clearAppointmentPagination(state) {
      state.currentPage = 1
      state.totalAppointments = 0
      state.userAppointments = []
      state.discarded = null
    },
    setBookingNowData(state, action: PayloadAction<BookingNowData>) {
      state.bookingNowData = action.payload
    },
  },
  extraReducers: builder => {
    asyncReducers(
      builder,
      getAppointmentColors,
      'loading',
      (state, action: PayloadAction<ReservationResponse>) => {
        if (action.payload.data) {
          const options: SelectTypes[] = []
          Object.entries(action.payload.data).forEach(([key, value]) =>
            options.push({ label: key, color: value, value })
          )
          state.reservationColors = options
        }
      }
    ),
      asyncReducers(
        builder,
        getDiscardedAppointments,
        'loading',
        (state, action: PayloadAction<DiscardedAppointmentResponse>) => {
          if (action.payload) {
            state.discarded = action.payload.data
            if (action.payload.description) {
              state.totalAppointments = action.payload.description.total_count
            }
          }
        }
      ),
      asyncReducers(
        builder,
        getUserAppointments,
        'loading',
        (state, action: PayloadAction<UserAppointmentResponse>) => {
          if (action.payload) {
            const { data } = action.payload
            if (data.length > 0) {
              state.userAppointments = data
            } else {
              state.userAppointments = []
            }
            if (action.payload.description) {
              state.totalAppointments = action.payload.description.total_count
            }
          }
        }
      ),
      asyncReducers(
        builder,
        getLocation,
        'loading',
        (state, action: PayloadAction<locationResponse>) => {
          if (action.payload) {
            if (action.payload.data.courts.length > 0) {
              const courtsOrdered = sortBy(
                action.payload.data.courts,
                court => court.id
              )
              const newCourts = courtsOrdered.map(court => {
                return {
                  id: court.id ? court.id.toString() : '',
                  title: court.name,
                }
              })
              state.courts = newCourts
            }
            state.location = action.payload.data
          }
        }
      ),
      asyncReducers(
        builder,
        updateLocation,
        'loading',
        (state, action: PayloadAction<locationResponse>) => {
          if (action.payload) {
            if (action.payload.error) {
              toast.error(getErrorMessage(action.payload.error))
              return
            }
            if (action.payload.data.courts.length > 0) {
              const courtsOrdered = sortBy(
                action.payload.data.courts,
                court => court.id
              )
              const newCourts = courtsOrdered.map(court => {
                return {
                  id: court.id ? court.id.toString() : '',
                  title: court.name,
                }
              })
              state.courts = newCourts
            }
            state.location = action.payload.data
            toast.success('The location was successfully updated')
          }
        }
      ),
      asyncReducers(
        builder,
        getAppointmentCategories,
        'loading',
        (state, action: PayloadAction<AppointmentCategory[]>) => {
          if (action.payload?.length > 0) {
            const options = action.payload
            const formatCategories = options.map(category => {
              return { label: category.name, value: category.id.toString() }
            })
            state.categories = formatCategories
          }
        }
      ),
      asyncReducers(
        builder,
        getSingleAppointment,
        'loadingAppointment',
        (state, action: PayloadAction<getSingleAppointmentResponse>) => {
          if (action.payload) {
            state.currentAppointment = action.payload.data
          }
        }
      ),
      asyncReducers(
        builder,
        updateAppointment,
        'loading',
        (state, action: PayloadAction<UserAppointmentResponse>) => {
          if (action.payload.error) {
            return toast.error(getErrorMessage(action.payload.error))
          }
          // Defense-in-depth: a move that didn't persist resolves WITHOUT the
          // enriched appointmentId (same signal handleDrop rolls back on), so
          // never claim success on it — product#502 symptom 2.
          if (!action.payload.appointmentId) return
          toast.success('Appointment Updated')
        }
      ),
      asyncReducers(
        builder,
        deleteAppointment,
        'loadingAppointment',
        (state, action: PayloadAction<UserAppointmentResponse>) => {
          if (action.payload.error) {
            toast.error(getErrorMessage(action.payload.error))
          }
        }
      )
    // getAppointments is handled explicitly (not via asyncReducers) so we can
    // take-latest: track the newest request's id in `pending` and drop any
    // fulfilled/rejected response that a newer request has superseded — a slow
    // fetch for the previous day can no longer paint under the new date header
    // (product#502).
    builder.addCase(getAppointments.pending, (state, action) => {
      state.loading = APILoadingStatus.Loading
      state.appointmentsReqId = action.meta.requestId
    })
    builder.addCase(
      getAppointments.fulfilled,
      (state, action: PayloadAction<AppointmentsResponse, string, { requestId: string }>) => {
        // Ignore a superseded response.
        if (action.meta.requestId !== state.appointmentsReqId) return
        state.loading = APILoadingStatus.Succeeded
        if (action.payload && action.payload.data) {
          const events = [...action.payload.data]
          if (events.length > 0) {
            const newEvents: EventCalendar[] = []
            events.map(event => {
              if (event.courts) {
                event.courts.forEach(courtId =>
                  newEvents.push({
                    ...event,
                    start: new Date(event.start),
                    end: new Date(event.end),
                    resourceId: courtId.toString(),
                  })
                )
              }
            })
            state.appointments = newEvents
            return
          }
          state.appointments = null
        }
      }
    )
    builder.addCase(getAppointments.rejected, (state, action) => {
      if (action.meta.requestId !== state.appointmentsReqId) return
      state.loading = APILoadingStatus.Failed
      // Surface the failure instead of silently leaving a possibly-stale board.
      toast.error('Could not refresh the calendar. Please try again.')
    })
    asyncReducers(
      builder,
      getOpenCourts,
      'loadingOpenCourts',
      (state, action: PayloadAction<getOpenCourtsResponse>) => {
        if (action.payload.error) {
          toast.error(getErrorMessage(action.payload.error))
        }
      }
    )
    asyncReducers(builder, postRestoreAppointment, 'loading')
  },
})

export default appointmentSlice.reducer

export const appointmentActions = {
  ...appointmentSlice.actions,
  ...appointmentSlice.reducer,
  getDiscardedAppointments,
  getUserAppointments,
  postRestoreAppointment,
  getAppointments,
  getLocation,
  getAppointmentCategories,
  getSingleAppointment,
  getAppointmentColors,
  updateAppointment,
  updateLocation,
  deleteAppointment,
  getOpenCourts,
}
